import React, { ReactNode, Suspense, useEffect, useMemo, useState } from 'react';
import {
  AdvancedCobranding,
  SimpleCobranding,
  useCobranding,
} from '../../utilities/cobranding/cobranding';
import { useAdvancedCobrandingContext } from './AdvancedCobrandingContext';
import Spinner from '@atlaskit/spinner';

type PageGuardProps = {
  children: ReactNode;
  requiredComponents: Array<string>;
  registrationStarted: boolean;
};

/**
 * Temporary solution to determine how long the PageGuard will give the components to register themselves.
 * We want to give cobranding time to load but no matter what we want to have a graceful fallback
 * This makes testing more complicated, therefore this constant exists.
 */
export const REGISTRATION_TIMEOUT = 2000;

const canShowOverlay = process.env.NODE_ENV !== 'production';

export const PageGuard = ({
  children,
  requiredComponents,
  registrationStarted,
}: PageGuardProps) => {
  const { cobranding } = useCobranding();
  const { components } = useAdvancedCobrandingContext();
  const [registrationTimedOut, setRegistrationTimedOut] = useState(false);

  const missingComponents = useMemo(() => {
    return requiredComponents.filter(requiredComponent => !components?.includes(requiredComponent));
  }, [requiredComponents, components]);

  useEffect(() => {
    if (!registrationStarted) {
      return;
    }

    const timeout = setTimeout(() => {
      setRegistrationTimedOut(true);
    }, REGISTRATION_TIMEOUT);

    return () => clearTimeout(timeout);
  }, [registrationStarted]);

  const hasRegisteredAllRequiredComponents = missingComponents.length === 0;
  const isWaitingForRegistration = !registrationTimedOut;

  useEffect(() => {
    if (!hasRegisteredAllRequiredComponents && !isWaitingForRegistration && !canShowOverlay) {
      console.warn(
        `[COBRANDING] ${window.location.pathname} has not registered all required components.`
      );
    }
  }, [cobranding, hasRegisteredAllRequiredComponents, isWaitingForRegistration]);

  if (hasRegisteredAllRequiredComponents) {
    return (
      <Suspense fallback={<LoadingOverlay />}>
        <div
          style={{ height: '100%' }} // important for children elements to flex-grow and fill the screen properly
          data-testid="registration-complete">
          {children}
        </div>
      </Suspense>
    );
  }

  if (isWaitingForRegistration || !canShowOverlay) {
    // Displays the loading overlay while the page is lazy-loading and while the registration is in progress.
    // OR if we can't show the overlay (i.e. this is prod/staging) then we let the experiment render
    return (
      <Suspense fallback={<LoadingOverlay />}>
        {children}
        <LoadingOverlay />
      </Suspense>
    );
  }

  return (
    <div data-testid="registration-complete">
      <MissingComponentErrorDisplay cobranding={cobranding} missingComponents={missingComponents}>
        {children}
      </MissingComponentErrorDisplay>
    </div>
  );
};

const MissingComponentErrorDisplay = ({
  cobranding,
  missingComponents,
  children,
}: {
  cobranding: SimpleCobranding | AdvancedCobranding;
  missingComponents: string[];
  children: ReactNode;
}) => {
  const search = new URLSearchParams(window.location.search);
  search.delete('token');

  return (
    <div style={{ position: 'relative' }}>
      <div
        style={{
          position: 'fixed',
          zIndex: 100,
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <div
          style={{
            position: 'fixed',
            zIndex: -1,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.25,
            backgroundColor: '#FFADB0',
          }}
        />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            padding: '4rem',
            borderRadius: '0.5rem',
            background: '#FFADB0',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow:
              'rgba(50, 50, 93, 0.25) 0px 50px 100px -20px, rgba(0, 0, 0, 0.3) 0px 30px 60px -30px',
          }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <h2 style={{ fontWeight: 'bold' }}>Advanced Cobranding</h2>
            <p>Unable to render page due to missing required components:</p>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.1rem',
              padding: '1rem',
              borderRadius: '0.25rem',
              color: 'white',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#c92216',
            }}>
            {missingComponents.map(component => (
              <p style={{ margin: 0 }} key={component}>
                <span data-testid="missing-component">{component}</span>
              </p>
            ))}
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.25rem',
            }}>
            <p style={{ margin: 0 }}>
              <strong>Cobranding</strong>: {cobranding}
            </p>
            <p style={{ margin: 0, display: 'flex', flexWrap: 'wrap' }}>
              <strong>URL</strong>: {`${window.location.pathname}?${search}`}
            </p>
          </div>
        </div>
      </div>
      {children}
    </div>
  );
};

const LoadingOverlay = () => (
  <div
    style={{
      position: 'fixed',
      top: 0,
      zIndex: 2,
      pointerEvents: 'none',
      left: 0,
      right: 0,
      bottom: 0,
      flexDirection: 'column',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'white',
    }}>
    <Spinner />
  </div>
);
